/**
 * @file        CasePage.jsx
 * @module      modules/kyb/manager/console
 * @description One case (a verification attempt): header with assignee, SLA
 *              and actions; status banners; the four checks with Override;
 *              Business / Identity / Link and authority / Documents tabs; and
 *              internal notes with the activity timeline — as in the shared
 *              HTML design.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { kybManagerApi } from "../api/kybManagerApi";
import { CHECK_KEYS, CHECK_NAMES, CS, Chip, Sla, fmtSize, fmtWhen, useToast } from "./ui";
import { ApproveModal, DocViewer, DocsModal, OverrideModal, PendingModal, RejectModal, ReopenModal, downloadDoc } from "./CaseModals";

const KV = ({ rows }) =>
  rows && rows.length ? (
    <dl className="kv">
      {rows.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  ) : (
    <p style={{ color: "var(--muted)", margin: 0 }}>Not available yet.</p>
  );

const fmtAt = (t) => {
  if (!t) return "";
  const d = new Date(t);
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

export default function CasePage({ me, onChanged }) {
  const { id } = useParams();
  const toast = useToast();
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);
  const [ptab, setPtab] = useState("business");
  const [modal, setModal] = useState(null); // { kind, arg }
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    kybManagerApi
      .case(id)
      .then((d) => {
        setC(d);
        setError(null);
      })
      .catch((e) => setError(e.status === 404 ? "This case doesn't exist." : e.message || "Couldn't load this case."));
  }, [id]);

  useEffect(() => {
    setC(null);
    load();
  }, [load]);

  const done = () => {
    setModal(null);
    load();
    onChanged && onChanged();
  };

  const assign = async (to) => {
    try {
      await kybManagerApi.assign([c.id], to || null);
      const name = c.team.find((t) => t.id === to)?.name;
      toast(to ? `Assigned to ${name}` : "Unassigned");
      done();
    } catch (e) {
      toast(e.message || "Couldn't assign.");
    }
  };

  const addNote = async () => {
    if (!note.trim()) return toast("Write a note first");
    try {
      await kybManagerApi.addNote(c.id, note.trim());
      setNote("");
      toast("Note added");
      load();
    } catch (e) {
      toast(e.message || "Couldn't add the note.");
    }
    return undefined;
  };

  if (error) {
    return (
      <>
        <Link className="back" to="/kyb-manager">
          ← Review queue
        </Link>
        <div className="empty">{error}</div>
      </>
    );
  }
  if (!c) return <div className="loading">Loading…</div>;

  const approved = c.status === "auto" || c.status === "manual";
  const a = c.actions;
  const open = (kind, arg) => setModal({ kind, arg });

  const acts = (
    <>
      {a.reopen ? (
        <button className="btn ghost" onClick={() => open("reopen")}>
          Reopen case
        </button>
      ) : null}
      {a.requestDocuments ? (
        <button className="btn ghost" onClick={() => open("docs")}>
          Request documents
        </button>
      ) : null}
      {a.reject ? (
        <button className="btn danger" onClick={() => open("reject")}>
          Reject
        </button>
      ) : null}
      {a.approve ? (
        <button className="btn primary" onClick={() => open("approve")}>
          Approve
        </button>
      ) : null}
      {a.moveToReview ? (
        <button className="btn primary" onClick={() => open("pending")}>
          Move back to review
        </button>
      ) : null}
    </>
  );

  const banners = [];
  if (c.source === "fallback") {
    banners.push(
      <div className="banner warn" key="fb">
        <div>
          <b>Verified by documents because automatic checks were unavailable</b>
          {c.fallback?.failed?.length ? `${c.fallback.failed.join(", ")} failed${c.fallback.when ? ` at ${fmtAt(c.fallback.when)}` : ""}. ` : ""}
          {c.fallback?.entered
            ? `The Customer entered the organization name (${c.fallback.entered.name}), PAN ${c.fallback.entered.pan}${c.fallback.entered.gstin ? `, GSTIN ${c.fallback.entered.gstin}` : ""} and type (${c.fallback.entered.type})${c.fallback.entered.person ? `, their own details (${c.fallback.entered.person.name}, born ${c.fallback.entered.person.dob}, PAN ${c.fallback.entered.person.pan})` : ""} and uploaded documents. `
            : c.fallback?.reasons?.length
              ? `${c.fallback.reasons.join(". ")}. `
              : ""}
          Check the documents against these details before approving.
        </div>
      </div>,
    );
  }
  if (c.status === "awaiting" && c.request) {
    banners.push(
      <div className="banner info" key="aw">
        <div>
          <b>Waiting on the Customer · SLA paused</b>
          Requested {fmtAt(c.request.at)}:
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {(c.request.items || c.request.docs.map((doc) => ({ doc, message: c.request.message, dueAt: c.request.dueAt }))).map((i) => (
              <li key={i.doc}>
                <b style={{ display: "inline", color: "inherit" }}>{i.doc}</b> — “{i.message}” · due {fmtAt(i.dueAt)}
              </li>
            ))}
          </ul>
        </div>
      </div>,
    );
  }
  if (c.waitingOnBusinessAdmin) {
    banners.push(
      <div className="banner info" key="join">
        <div>
          <b>Waiting on the business admin</b>
          {c.person.name || "This person"} asked to join {c.business.name || "a verified business"}. Its business admin approves or declines the request from their account; you can still reject it.
        </div>
      </div>,
    );
  }
  if (c.status === "auto") {
    banners.push(
      <div className="banner ok" key="auto">
        <div>
          <b>Approved automatically</b>All four checks passed from records. You can still request documents or move it back to review; bookings pause until it's approved again.
        </div>
      </div>,
    );
  }
  if (c.status === "manual" && c.approvedBy) {
    banners.push(
      <div className="banner ok" key="man">
        <div>
          <b>Approved by {c.approvedBy.name}</b>
          {c.approvedBy.reason || "All checks passed."}
        </div>
      </div>,
    );
  }
  if (c.status === "rejected") {
    banners.push(
      <div className="banner bad" key="rej">
        <div>
          <b>Rejected</b>
          {c.rejection ? `${c.rejection.category}: ${c.rejection.message}` : "Declined."}
        </div>
      </div>,
    );
  }
  if (c.status === "progress" && c.request) {
    banners.push(
      <div className="banner info" key="preq">
        <div>
          <b>Documents requested</b>
          Requested {fmtAt(c.request.at)}. The Customer sees this before their next step:
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {(c.request.items || c.request.docs.map((doc) => ({ doc, message: c.request.message, dueAt: c.request.dueAt }))).map((i) => (
              <li key={i.doc}>
                <b style={{ display: "inline", color: "inherit" }}>{i.doc}</b> — “{i.message}” · due {fmtAt(i.dueAt)}
              </li>
            ))}
          </ul>
        </div>
      </div>,
    );
  }
  if (c.status === "progress") {
    banners.push(
      <div className="banner info" key="prog">
        <div>
          <b>The Customer is still verifying</b>They're at step {c.stage} of 5. You can request documents now, or wait until they submit.
        </div>
      </div>,
    );
  }
  if (c.business.bookingsPaused && !approved) {
    banners.push(
      <div className="banner warn" key="bp">
        <div>
          <b>Bookings paused</b>This business can't book until it's approved again.
        </div>
      </div>,
    );
  }

  const tabs = [
    ["business", "Business"],
    ["person", "Identity"],
    ["link", "Link and authority"],
    ["docs", `Documents (${c.docs.length + c.requestedWaiting.length})`],
  ];

  let pbody;
  if (ptab === "business") {
    pbody = (
      <>
        <h4>Records</h4>
        <KV rows={c.panels.business} />
        {c.panels.nameCompare ? (
          <>
            <h4>Name comparison</h4>
            <table className="cmp">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Name</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {c.panels.nameCompare.map((r) => (
                  <tr key={r.source}>
                    <td>{r.source}</td>
                    <td>{r.name || "Not available"}</td>
                    <td className={`m ${r.match === false ? "n" : r.match ? "y" : ""}`}>{r.match === null ? (r.name ? "" : "–") : r.match ? "✓" : "✕"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}
      </>
    );
  } else if (ptab === "person") {
    pbody = (
      <>
        <h4>Person</h4>
        <KV rows={c.panels.identity} />
        <h4>Role stated by the Customer</h4>
        <KV rows={c.panels.role} />
        <h4>Contact</h4>
        <KV rows={[["E-mail", c.person.email], ["Mobile", c.person.mobile]].filter((r) => r[1])} />
      </>
    );
  } else if (ptab === "link") {
    pbody = (
      <>
        <h4>How the person was linked and authorized</h4>
        <KV rows={c.panels.link} />
      </>
    );
  } else {
    pbody = (
      <>
        <div className="docs">
          {c.docs.map((d) => (
            <div className="doc" key={d.id}>
              <div className="thumb">{(d.mimeType || "").includes("pdf") ? "PDF" : "Image"} preview</div>
              <b title={d.name || ""}>{d.label}</b>
              <small>{[d.name, fmtSize(d.sizeBytes)].filter(Boolean).join(" · ")}</small>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn small ghost" onClick={() => open("view", d)}>
                  View
                </button>
                <button className="btn small ghost" onClick={() => downloadDoc(d).then(() => toast("Download started"), (e) => toast(e.message || "Couldn't download."))}>
                  Download
                </button>
              </div>
            </div>
          ))}
          {c.requestedWaiting.map((d) => (
            <div className="doc waiting" key={`w-${d}`}>
              <div className="thumb">Requested</div>
              <b>{d}</b>
              <small>Waiting on the Customer</small>
            </div>
          ))}
        </div>
        {c.docs.length + c.requestedWaiting.length ? null : <p style={{ color: "var(--muted)", margin: 0 }}>No documents. Everything was verified from records.</p>}
      </>
    );
  }

  return (
    <>
      <Link className="back" to="/kyb-manager">
        ← Review queue
      </Link>
      <div className="casehead">
        <div style={{ minWidth: 0 }}>
          <h1>{c.business.name || "Business not entered yet"}</h1>
          <div className="meta">
            <span>
              <b>{c.caseNo}</b>
            </span>
            {c.business.pan ? (
              <span>
                PAN <b>{c.business.pan}</b>
              </span>
            ) : null}
            {c.business.type ? (
              <span>
                Organization type <b>{c.business.type}</b>
              </span>
            ) : null}
            <span>
              Submitted {fmtWhen(c.submittedAt)} by <b>{c.person.name || "—"}</b>
            </span>
            <Chip status={c.status} />
            {c.source === "fallback" ? <span className="tag fallback">API fallback</span> : null}
          </div>
          <div className="sideinfo">
            <div>
              <label htmlFor="asg">Assignee</label>
              {me?.role === "kyb_executive" ? (
                // Executives can't assign anyone else: show the assignee, and "Assign to me" only when unassigned.
                c.assignee ? (
                  <span id="asg" style={{ display: "inline-flex", alignItems: "center", height: 34, fontWeight: 600 }}>
                    {c.assignee.name}
                  </span>
                ) : (
                  <button id="asg" className="btn small ghost" onClick={() => assign(me.id)}>
                    Assign to me
                  </button>
                )
              ) : (
                <select id="asg" value={c.assignee?.id || ""} onChange={(e) => assign(e.target.value)}>
                  <option value="">Unassigned</option>
                  {c.team.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label>SLA</label>
              <Sla status={c.status} dueAt={c.slaDueAt} />
            </div>
          </div>
        </div>
        <div className="acts">{acts}</div>
      </div>
      {banners}

      <div className="verdict" aria-label="The four checks">
        {CHECK_KEYS.map((k) => {
          const ck = c.checks[k];
          const v = CS[ck.status] || CS.pending;
          return (
            <div className={`vc ${v[0]}`} key={k}>
              <div className="top">
                <span>{CHECK_NAMES[k]}</span>
                {a.override ? (
                  <button className="btn small ghost" onClick={() => open("override", k)}>
                    Override
                  </button>
                ) : null}
              </div>
              <h3>
                <span className={`ico ${v[0]}`} aria-hidden="true">
                  {v[1]}
                </span>
                {v[2]}
              </h3>
              <p>{ck.note}</p>
              {ck.override ? (
                <span className="ovr">
                  Overridden by {ck.override.by} · {ck.override.reason}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="cols">
        <section className="panel">
          <div className="ptabs" role="tablist">
            {tabs.map(([k, label]) => (
              <button key={k} role="tab" aria-selected={ptab === k} className={ptab === k ? "on" : ""} onClick={() => setPtab(k)}>
                {label}
              </button>
            ))}
          </div>
          <div className="pbody">{pbody}</div>
        </section>
        <section className="panel">
          <div className="pbody">
            <h2>Notes and activity</h2>
            <div className="note">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note. The Customer won't see it." aria-label="Internal note" />
              <div className="row">
                <small>Visible to the KYC team only</small>
                <button className="btn small primary" onClick={addNote}>
                  Add note
                </button>
              </div>
            </div>
            <ul className="timeline">
              {c.activity.map((x, i) => (
                <li key={`${x.at}-${i}`} className={x.kind}>
                  {x.text}
                  <small>
                    {x.kind === "sys" ? "System" : x.kind === "cust" ? "Customer" : "Team"} · {fmtWhen(x.at)}
                  </small>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      {modal?.kind === "docs" ? <DocsModal c={c} onClose={() => setModal(null)} onDone={done} /> : null}
      {modal?.kind === "override" ? <OverrideModal c={c} check={modal.arg} onClose={() => setModal(null)} onDone={done} /> : null}
      {modal?.kind === "approve" ? <ApproveModal c={c} onClose={() => setModal(null)} onDone={done} /> : null}
      {modal?.kind === "reject" ? <RejectModal c={c} onClose={() => setModal(null)} onDone={done} /> : null}
      {modal?.kind === "pending" ? <PendingModal c={c} onClose={() => setModal(null)} onDone={done} /> : null}
      {modal?.kind === "reopen" ? <ReopenModal c={c} onClose={() => setModal(null)} onDone={done} /> : null}
      {modal?.kind === "view" ? <DocViewer doc={modal.arg} onClose={() => setModal(null)} /> : null}
    </>
  );
}
